import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useIsMobile } from '@sravni/react-utils';
import { StepContext } from '../../../creator/step.context';
import { useFormContext } from 'react-hook-form';
import { FORM_FIELDS, FORM_STEPS, PAGE } from '../../../../../constants';
import { FocusFieldWrapper, StepContainer, StepSubmitBlock, StepSubtitle } from '../common';
import { Col, Row, Rows } from '../../../../landing.common';
import {
    AdditionalPhoneField,
    AdditionalPhoneOwnerField,
    AdditionalPhoneOwnerFioField,
    EducationField,
    EmailField,
    FamilyStatusField,
    NumberOfDependentsField,
    RegistrationApartmentField,
    RegistrationDateField,
    RegistrationStreetField,
    ResidenceApartmentField,
    ResidenceSameRegistrationField,
    ResidenceStreetField,
} from './fields';
import { useSelection } from '../../../../../store/reducers/selection';
import { ADDITIONAL_PHONE_OWNER_DEPS } from '../../../../../constants/field.dependencies';
import { DesktopSubmitView } from '../../components/form/components/desktop.submit';

export const PersonalDataStepView: FC = React.memo(() => {
    const { page } = useSelection();
    const isMobile = useIsMobile();
    const { currentStep, currentStepConfig } = useContext(StepContext);

    const form = useFormContext();

    const {
        setValue,
        getValues,
        watch,
        formState: { dirtyFields: _, errors },
        setFocus,
    } = form;

    useEffect(() => {
        if (FORM_STEPS.PERSONAL_DATA === currentStepConfig.stepName) {
            if (!isMobile) {
                try {
                    setFocus(FORM_FIELDS.REGISTRATION_STREET);
                } catch (e) {
                    // nothing
                }
            }
        }
    }, [currentStep]);

    const [addressesAreEqual, additionalPhoneOwner] = watch([
        FORM_FIELDS.RESIDENCE_SAME_REGISTRATION,
        FORM_FIELDS.ADDITIONAL_PHONE_OWNER,
    ]);

    useEffect(() => {
        if (!getValues(FORM_FIELDS.RESIDENCE_SAME_REGISTRATION)) {
            return;
        }
        const hasResidenceErrors =
            [FORM_FIELDS.RESIDENCE_STREET, FORM_FIELDS.RESIDENCE_APARTMENT].some((key) => errors[key]) &&
            ![FORM_FIELDS.REGISTRATION_STREET, FORM_FIELDS.REGISTRATION_APARTMENT].some((key) => errors[key]);
        if (hasResidenceErrors) {
            setValue(FORM_FIELDS.RESIDENCE_SAME_REGISTRATION, false);
        }
    }, [errors]);

    const additionalPhoneFields = useMemo(
        () => ADDITIONAL_PHONE_OWNER_DEPS[additionalPhoneOwner] || [],
        [additionalPhoneOwner],
    );

    return (
        <StepContainer>
            <Rows gap="16">
                <StepSubtitle>Адрес регистрации</StepSubtitle>
                <Row gap="16" mobileGap="12">
                    <Col md-8>
                        <FocusFieldWrapper name={FORM_FIELDS.REGISTRATION_STREET} field={<RegistrationStreetField />} />
                    </Col>
                    <Col md-4>
                        <FocusFieldWrapper
                            name={FORM_FIELDS.REGISTRATION_APARTMENT}
                            field={<RegistrationApartmentField />}
                        />
                    </Col>
                </Row>
                <Row gap="16" mobileGap="12">
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.REGISTRATION_DATE} field={<RegistrationDateField />} />
                    </Col>
                </Row>
                <Row gap="16" mobileGap="12">
                    <Col md-8>
                        <ResidenceSameRegistrationField />
                    </Col>
                </Row>
                {!addressesAreEqual && (
                    <Row gap="16" mobileGap="12">
                        <Col md-8>
                            <FocusFieldWrapper name={FORM_FIELDS.RESIDENCE_STREET} field={<ResidenceStreetField />} />
                        </Col>
                        <Col md-4>
                            <FocusFieldWrapper
                                name={FORM_FIELDS.RESIDENCE_APARTMENT}
                                field={<ResidenceApartmentField />}
                            />
                        </Col>
                    </Row>
                )}
            </Rows>
            <Rows gap="16">
                <StepSubtitle>Дополнительная информация</StepSubtitle>
                <Row gap="16" mobileGap="12">
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.EDUCATION} field={<EducationField />} />
                    </Col>
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.FAMILY_STATUS} field={<FamilyStatusField />} />
                    </Col>
                    {page !== PAGE.AVITO && (
                        <Col md-4>
                            <FocusFieldWrapper
                                name={FORM_FIELDS.NUMBER_OF_DEPENDENTS}
                                field={<NumberOfDependentsField />}
                            />
                        </Col>
                    )}
                </Row>
                <Row gap="16" mobileGap="12">
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.EMAIL} field={<EmailField />} />
                    </Col>
                    {isMobile ? (
                        <>
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER}
                                    field={<AdditionalPhoneOwnerField />}
                                />
                            </Col>
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.ADDITIONAL_PHONE}
                                    field={<AdditionalPhoneField />}
                                />
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.ADDITIONAL_PHONE}
                                    field={<AdditionalPhoneField />}
                                />
                            </Col>
                            <Col md-4>
                                <FocusFieldWrapper
                                    name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER}
                                    field={<AdditionalPhoneOwnerField />}
                                />
                            </Col>
                        </>
                    )}
                </Row>
                {additionalPhoneFields.includes(FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO) && (
                    <Row gap="16" mobileGap="12">
                        <Col>
                            <FocusFieldWrapper
                                name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO}
                                field={<AdditionalPhoneOwnerFioField />}
                            />
                        </Col>
                    </Row>
                )}
            </Rows>
            <StepSubmitBlock>
                <DesktopSubmitView />
            </StepSubmitBlock>
        </StepContainer>
    );
});

PersonalDataStepView.displayName = 'PersonalDataStepView';
